import { Box } from '@nimbus-ds/components';
import { ExclamationTriangleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { Card, Input } from '@tiendanube/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { HandleChangeInterface } from '../../hooks/useProductForm/types';
import ApplyValuesToAllVariants from '../ApplyValuesToAllVariants';
import { OnChangeToAllVariants } from '../Variants/types';

interface ProductCodesProps {
  barcode: string | null;
  sku: string | null;
  appearance?: 'column' | 'row';
  onChange: (data: HandleChangeInterface) => void;
  onChangeToAllVariants?: OnChangeToAllVariants;
}

const isValidProductCode = (value: string) => {
  const regex = /^[a-zA-Z0-9]*$/;
  return regex.exec(value) !== null;
};

function ProductCodes({
  sku,
  barcode,
  appearance = 'column',
  onChange,
  onChangeToAllVariants,
}: ProductCodesProps): JSX.Element | null {
  const t = useTranslationCatalog();
  const hasError = !isValidProductCode(barcode || '');

  const columnGrid = {
    xs: '1fr',
    md: appearance === 'row' ? '1fr 1fr' : '1fr',
  };

  return (
    <Card title={t('products.detail.productCodes')}>
      <Box display="grid" gridTemplateColumns={columnGrid} gridGap="4">
        <Box display="flex" flexDirection="column" gap="2">
          <Input
            type="text"
            label={t('products.detail.sku')}
            placeholder=""
            helpText={t('products.detail.skuInfo')}
            value={sku || ''}
            onChange={onChange}
            name="sku"
          />
          {onChangeToAllVariants && (
            <ApplyValuesToAllVariants
              value={sku || ''}
              onChange={onChangeToAllVariants('sku', sku)}
            />
          )}
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          <FormField.Input
            type="text"
            name="barcode"
            value={barcode || ''}
            label={t('products.detail.barcode')}
            placeholder=""
            helpText={
              hasError
                ? t('products.detail.barcodeWarning')
                : t('products.detail.barcodeInfo')
            }
            showHelpText
            appearance={hasError ? 'warning' : undefined}
            helpIcon={hasError ? ExclamationTriangleIcon : undefined}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ name: 'barcode', value: event.target.value })
            }
          />
          {onChangeToAllVariants && (
            <ApplyValuesToAllVariants
              value={barcode || ''}
              onChange={onChangeToAllVariants('barcode', barcode)}
            />
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default ProductCodes;
